/**
 * GlobalStreamingManager
 * 
 * Centralized file streaming and download manager for all domains.
 * Handles viewing and downloading files across Tasks, Clients, Matters, Files, Teams, Departments, etc.
 * 
 * Patterns supported:
 * - Task attachments: /tasks/task/{taskId}/attachments/{attachmentId}/{action}
 * - Client documents: /clients/{clientUuid}/documents/{documentUuid}/{action}
 * - Matter sources: /matters/{matterUuid}/sources/{sourceId}/{action}
 * - File streams: /files/{fileId}/{action}
 */

import apis from '../../utils/apis';

export interface StreamingRequest {
  /** Domain type: 'tasks', 'clients', 'matters', 'files', 'teams', 'departments', 'support' */
  domain: 'tasks' | 'clients' | 'matters' | 'files' | 'teams' | 'departments' | 'support';
  
  /** Resource ID (taskId, clientUuid, matterUuid, etc.) */
  resourceId: string;
  
  /** Attachment/Document/File ID */
  fileId: string;
  
  /** Action: 'view' or 'download' */
  action: 'view' | 'download';
  
  /** Original filename for download */
  filename: string;
  
  /** Optional: Content type override */
  contentType?: string;
  
  /** Optional: Sub-resource ID for complex domains */
  subResourceId?: string;
}

export interface StreamingResponse {
  success: boolean;
  blob?: Blob;
  contentType?: string;
  filename?: string;
  error?: string;
}

/**
 * Build the API endpoint based on domain and action
 */
const buildEndpoint = (req: StreamingRequest): string => {
  const { domain, resourceId, fileId, action, subResourceId } = req;

  switch (domain) {
    case 'tasks':
      return `/tasks/task/${resourceId}/attachments/${fileId}/${action}`;
    
    case 'clients':
      return `/clients/${resourceId}/documents/${fileId}/${action === 'view' ? 'view-content' : ''}`.replace(/\/$/, '');
    
    case 'matters':
      return `/matters/${resourceId}/sources/${fileId}/${action}`;
    
    case 'files':
      return `/files/${fileId}/${action}`;
    
    case 'teams':
      return `/org-people/teams/${resourceId}/documents/${fileId}/${action}`;
    
    case 'departments':
      return `/org-people/departments/${resourceId}/documents/${fileId}/${action}`;
    
    case 'support':
      return `/support/tickets/${resourceId}/attachments/${fileId}/${action}`;
    
    default:
      throw new Error(`Unknown domain: ${domain}`);
  }
};

/**
 * Validate the response blob
 */
const validateBlob = (blob: Blob | null): { valid: boolean; error?: string } => {
  if (!blob || !(blob instanceof Blob)) {
    return { valid: false, error: 'Invalid file content received from server' };
  }

  if (blob.size === 0) {
    return { valid: false, error: 'File is empty or could not be retrieved' };
  }

  // Check if server returned an HTML error page instead of the file
  if (blob.type === 'text/html' || blob.type === '') {
    return { valid: false, error: 'Server returned an error. File may not exist or access was denied.' };
  }

  return { valid: true };
};

/**
 * Extract filename from Content-Disposition header
 */
const extractFilenameFromHeader = (disposition: string | undefined, fallback: string): string => {
  if (!disposition) return fallback;
  
  const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
  if (filenameMatch) {
    return filenameMatch[1]?.replace(/['"]/g, '') || fallback;
  }
  
  return fallback;
};

/**
 * Stream/View a file (returns blob for modal/viewer)
 */
export const streamFile = async (req: StreamingRequest): Promise<StreamingResponse> => {
  try {
    if (req.action !== 'view') {
      throw new Error('streamFile action must be "view"');
    }

    const endpoint = buildEndpoint(req);

    const response = await apis({
      method: 'GET',
      url: endpoint,
      responseType: 'blob',
    });

    if (!response) {
      return {
        success: false,
        error: 'No response from server',
      };
    }

    if (response.status !== 200) {
      return {
        success: false,
        error: `Server returned status ${response.status}`,
      };
    }

    const blob = response.data;
    const validation = validateBlob(blob);
    
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }

    // Get content-type from response headers
    const contentType = ((response.headers['content-type'] as string) || '').toLowerCase().trim() || req.contentType || 'application/octet-stream';

    // Extract filename from Content-Disposition header
    const filename = extractFilenameFromHeader(
      response.headers['content-disposition'],
      req.filename
    );

    return {
      success: true,
      blob,
      contentType,
      filename,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Error streaming file',
    };
  }
};

/**
 * Download a file (triggers browser download)
 */
export const downloadFile = async (req: StreamingRequest): Promise<StreamingResponse> => {
  try {
    if (req.action !== 'download') {
      throw new Error('downloadFile action must be "download"');
    }

    const endpoint = buildEndpoint(req);

    const response = await apis({
      method: 'GET',
      url: endpoint,
      responseType: 'blob',
    });

    if (!response) {
      return {
        success: false,
        error: 'No response from server',
      };
    }

    if (response.status !== 200) {
      return {
        success: false,
        error: `Download failed with status ${response.status}`,
      };
    }

    const blob = response.data;
    const validation = validateBlob(blob);
    
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }

    // Extract filename from Content-Disposition header if available
    const filename = extractFilenameFromHeader(
      response.headers['content-disposition'],
      req.filename
    );

    // Trigger browser download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      filename,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Error downloading file',
    };
  }
};

/**
 * Unified stream or download handler
 */
export const handleStream = async (req: StreamingRequest): Promise<StreamingResponse> => {
  if (req.action === 'view') {
    return streamFile(req);
  } else if (req.action === 'download') {
    return downloadFile(req);
  } else {
    return {
      success: false,
      error: 'Invalid action. Must be "view" or "download"',
    };
  }
};

/**
 * Stream or download with dispatch support (for use with Redux)
 */
export const createStreamingThunk = (req: StreamingRequest) => async (dispatch: any) => {
  return handleStream(req);
};

export default {
  streamFile,
  downloadFile,
  handleStream,
  createStreamingThunk,
};
